import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';

const TOAST_CLASS_NAME = 'spotlight-action-toast-shell';

const toastTypeIcon = {
  error: ErrorIcon,
  info: InfoIcon,
  success: CheckCircleIcon,
};

const toastTypeTitle = {
  error: 'Something went wrong',
  info: 'Heads up',
  success: 'Done',
};

const useStyles = makeStyles((theme) => ({
  '@global': {
    '.Toastify__toast-container--bottom-right': {
      bottom: 16,
      maxWidth: 'calc(100vw - 32px)',
      right: 16,
      width: 'auto',
      [theme.breakpoints.down('xs')]: {
        bottom: 12,
        left: 12,
        right: 12,
        width: 'calc(100vw - 24px)',
      },
    },
    [`.Toastify__toast-container .${TOAST_CLASS_NAME}`]: {
      alignItems: 'flex-start',
      backdropFilter: 'blur(18px)',
      background: theme.palette.type === 'dark'
        ? 'rgba(31, 31, 31, 0.92)'
        : 'rgba(255, 255, 255, 0.94)',
      border: `1px solid ${theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.10)'
        : 'rgba(17, 24, 39, 0.10)'}`,
      borderRadius: 18,
      boxShadow: theme.palette.type === 'dark'
        ? '0 18px 42px rgba(0, 0, 0, 0.36)'
        : '0 18px 42px rgba(15, 23, 42, 0.18)',
      color: theme.palette.text.primary,
      display: 'flex',
      maxWidth: 'calc(100vw - 32px)',
      minHeight: 84,
      overflow: 'visible',
      padding: theme.spacing(1.75, 2.25),
      width: 'fit-content',
      WebkitBackdropFilter: 'blur(18px)',
      [theme.breakpoints.down('xs')]: {
        maxWidth: 'calc(100vw - 24px)',
        minHeight: 80,
        padding: theme.spacing(1.75, 1.75),
        width: '100%',
      },
    },
    [`.Toastify__toast-container .${TOAST_CLASS_NAME} .Toastify__toast-body`]: {
      margin: 0,
      padding: 0,
      width: '100%',
    },
    [`.Toastify__toast-container .${TOAST_CLASS_NAME} .Toastify__toast-icon`]: {
      display: 'none',
    },
    [`.Toastify__toast-container .${TOAST_CLASS_NAME} .Toastify__close-button`]: {
      alignItems: 'center',
      alignSelf: 'flex-start',
      backgroundColor: 'transparent',
      color: theme.palette.type === 'dark' ? '#a8a8ad' : '#5f6875',
      display: 'flex',
      flexShrink: 0,
      height: 32,
      justifyContent: 'center',
      marginLeft: theme.spacing(1),
      opacity: 1,
      padding: 4,
      width: 32,
      '&:hover': {
        opacity: 0.9,
      },
    },
    [`.Toastify__toast-container .${TOAST_CLASS_NAME} .Toastify__close-button svg`]: {
      height: 18,
      width: 18,
    },
  },
  '@keyframes toastSlideIn': {
    from: {
      opacity: 0,
      transform: 'translate3d(18px, 12px, 0)',
    },
    to: {
      opacity: 1,
      transform: 'translate3d(0, 0, 0)',
    },
  },
  '@keyframes iconPulse': {
    '0%': {
      transform: 'scale(0.86)',
    },
    '45%': {
      transform: 'scale(1.18)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
  root: {
    alignItems: 'flex-start',
    animation: '$toastSlideIn 220ms ease-out both',
    display: 'flex',
    gap: theme.spacing(2),
    maxWidth: 460,
    minWidth: 0,
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1.5),
      maxWidth: '100%',
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  iconWrap: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    paddingTop: 4,
    '& svg': {
      animation: '$iconPulse 580ms ease-out both',
      flexShrink: 0,
      fontSize: 30,
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
      },
    },
  },
  successIcon: {
    color: '#00CCDD',
  },
  inactiveIcon: {
    color: theme.palette.type === 'dark' ? '#a8a8ad' : '#5f6875',
  },
  errorIcon: {
    color: '#ff5a5f',
  },
  infoIcon: {
    color: '#31b7f3',
  },
  content: {
    flex: '1 1 auto',
    minWidth: 0,
    paddingTop: 1,
  },
  title: {
    color: theme.palette.type === 'dark' ? '#f7f7f8' : '#1f2937',
    fontSize: 18,
    fontWeight: 800,
    lineHeight: 1.2,
    marginBottom: 6,
    [theme.breakpoints.down('xs')]: {
      fontSize: 17,
    },
  },
  message: {
    color: theme.palette.type === 'dark' ? '#ababaf' : '#5f6875',
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.3,
    wordBreak: 'break-word',
    [theme.breakpoints.down('xs')]: {
      fontSize: 14,
    },
  },
}));

const getIconClassName = (classes, active, type) => {
  if (type === 'error') return classes.errorIcon;
  if (type === 'info') return classes.infoIcon;
  return active ? classes.successIcon : classes.inactiveIcon;
};

const ToastDialog = ({
  active,
  activateIcon,
  deactivateIcon,
  activateText,
  deactivateText,
  title,
  type,
}) => {
  const classes = useStyles();
  const Icon = active ? toastTypeIcon[type] || toastTypeIcon.success : CancelIcon;
  const icon = active ? activateIcon : deactivateIcon;
  const fallbackTitle = toastTypeTitle[type] || toastTypeTitle.success;
  const toastTitle = active ? activateText : deactivateText;

  return (
    <Box className={classes.root} role={type === 'error' ? 'alert' : 'status'}>
      <Box className={`${classes.iconWrap} ${getIconClassName(classes, active, type)}`}>
        {icon || <Icon />}
      </Box>

      <Box className={classes.content}>
        <Typography className={classes.title}>{toastTitle || fallbackTitle}</Typography>
        {title && <Typography className={classes.message}>{title}</Typography>}
      </Box>
    </Box>
  );
};

ToastDialog.propTypes = {
  active: PropTypes.bool,
  activateIcon: PropTypes.element,
  deactivateIcon: PropTypes.element,
  activateText: PropTypes.string,
  deactivateText: PropTypes.string,
  title: PropTypes.node,
  type: PropTypes.oneOf(['success', 'error', 'info']),
};

ToastDialog.defaultProps = {
  active: true,
  activateIcon: null,
  deactivateIcon: null,
  activateText: '',
  deactivateText: '',
  title: '',
  type: 'success',
};

export default ToastDialog;
